// src/services/signalSession.js — ajouter ces fonctions

import {
  SignalProtocolAddress,
  SessionBuilder,
  SessionCipher,
  Curve,
} from '@privacyresearch/libsignal-protocol-typescript';
import { signalStore } from './signalStore';


import {
  getCachedFileKey,
  setCachedFileKey,
purgeSessionsAndPreKeys
} from './signalStore';
import {
aesEncryptBytes, aesDecryptBytes, aesEncryptText, aesDecryptText,
  selfEncryptKey, selfDecryptKey,
  bufferToBase64, base64ToBuffer,
} from './signalCrypto';
import api from './api';
import { openSelfWrap, saveSelfWrap } from './selfWrap';
import { toArrayBuffer } from './signalCrypto';


// Convertit une string binaire Signal en base64 (byte par byte, sûr)
function binaryStringToBase64(str) {
  const bytes = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) {
    bytes[i] = str.charCodeAt(i) & 0xFF;
  }
  return bufferToBase64(bytes);
}

// Convertit du base64 vers une string binaire Signal
function base64ToBinaryString(base64) {
  try {
    const bytes = base64ToBuffer(base64);
    let str = '';
    for (let i = 0; i < bytes.length; i++) {
      str += String.fromCharCode(bytes[i]);
    }
    return str;
  } catch (err) {
    console.error('base64ToBinaryString a échoué sur:', JSON.stringify(base64), 'longueur:', base64?.length);
    throw err;
  }
}

// signalSession.js
const _inFlightFileKeyDecrypts = new Map(); // fileId -> Promise<Uint8Array>

async function resolveFileKey(fileData, senderId, currentUserId, fileId, isGroup, selfWrapUrl) {
  const cached = await getCachedFileKey(fileId);
  if (cached) return cached;

  if (_inFlightFileKeyDecrypts.has(fileId)) return _inFlightFileKeyDecrypts.get(fileId);

  const promise = (async () => {
    const isSender = String(senderId) === String(currentUserId);
    let fileKey = null;
    let viaSignal = false;

    if (isSender) {
      fileKey = await selfDecryptKey(
        base64ToBuffer(fileData.keyForSender),
        base64ToBuffer(fileData.keyForSenderIv)
      );
    } else {
      fileKey = await openSelfWrap(fileData, currentUserId);   // NOUVEAU
      if (!fileKey) {
        if (isGroup) {
          const myKey = fileData.keyForMembers?.find(i => String(i.userId) === String(currentUserId));
          if (!myKey) throw new Error(`Aucune clé de fichier pour ${currentUserId}`);
          fileKey = myKey.keyType === null
            ? await selfDecryptKey(base64ToBuffer(myKey.encryptedKey), base64ToBuffer(myKey.keyIv))
            : (viaSignal = true, await decryptKeyFromUser(myKey.encryptedKey, myKey.keyType, String(senderId)));
        } else {
          viaSignal = true;
          fileKey = await decryptKeyFromUser(
            fileData.keyForRecipient, fileData.keyForRecipientType, String(senderId)
          );
        }
      }
    }

    if (!fileKey || fileKey.byteLength !== 32) {
      throw new Error(`Clé fichier invalide : ${fileKey?.byteLength || 0} octets`);
    }

    await setCachedFileKey(fileId, fileKey);
if (viaSignal && selfWrapUrl) saveSelfWrap(selfWrapUrl, fileKey);

    //if (viaSignal) saveSelfWrap(`/api/files/${fileId}/keyforself`, fileKey);
    return fileKey;
  })();

  _inFlightFileKeyDecrypts.set(fileId, promise);
  try { return await promise; } finally { _inFlightFileKeyDecrypts.delete(fileId); }
}

export async function buildSessionWithContact(contactUserId) {
if (!contactUserId || contactUserId === 'undefined') {
    console.error('buildSessionWithContact appelé avec un id invalide', contactUserId, new Error().stack);
    throw new Error('contactUserId invalide');
  }
  const address = new SignalProtocolAddress(
    String(contactUserId),
    1
  );

  return withSignalLock(
    address.toString(),
    () => buildSessionWithContactUnlocked(contactUserId)
  );
}
export async function encryptMessage(recipientUserId, plaintext) {
  const deviceId = 1;
  const address = new SignalProtocolAddress(recipientUserId, deviceId);

  const sessionRecord = await signalStore.loadSession(address.toString());
  if (!sessionRecord) {
    await buildSessionWithContact(recipientUserId);
  }

  const { ciphertext, iv, key } = await aesEncryptText(plaintext);

  return withSignalLock(address.toString(), async () => {
    const sessionCipher = new SessionCipher(signalStore, address);
    const signalEncryptedKey = await sessionCipher.encrypt(
      key.buffer.slice(key.byteOffset, key.byteOffset + key.byteLength)
    );
    const keyBodyBase64 = binaryStringToBase64(signalEncryptedKey.body);
    const selfEncrypted = await selfEncryptKey(key);

    return {
      encryptedText: bufferToBase64(ciphertext),
      e2eeData: {
        textIv: bufferToBase64(iv),
        keyForRecipient: keyBodyBase64,
        keyForRecipientType: signalEncryptedKey.type,
        keyForSender: bufferToBase64(selfEncrypted.encrypted),
        keyForSenderIv: bufferToBase64(selfEncrypted.iv),
      },
    };
  });
}
/**
 * Déchiffre un message.
 * Détecte automatiquement si on est destinataire ou expéditeur.
 */
const _decryptAttempts = new Map();

export async function decryptMessage(e2eeData, encryptedText, senderId, currentUserId, messageId) {
  if (e2eeData?.group === true) {
    throw new Error('decryptMessage() appelé sur un message groupe');
  }

  let aesKey;
  let viaSignal = false;

  if (String(senderId) === String(currentUserId)) {
    aesKey = await selfDecryptKey(
      base64ToBuffer(e2eeData.keyForSender),
      base64ToBuffer(e2eeData.keyForSenderIv)
    );
  } else {
    // 1) l'enveloppe self d'abord : elle survit au recovery
    aesKey = await openSelfWrap(e2eeData, currentUserId);

    // 2) sinon Signal (opération à usage unique)
    if (!aesKey) {
      const address = new SignalProtocolAddress(senderId, 1);

    aesKey = await withSignalLock(address.toString(), async () => {
      const sessionCipher = new SessionCipher(signalStore, address);
      const bodyBinary = base64ToBinaryString(e2eeData.keyForRecipient);
      const msgType = Number(e2eeData.keyForRecipientType);

      const keyBuffer = msgType === 3
        ? await sessionCipher.decryptPreKeyWhisperMessage(bodyBinary, 'binary')
        : await sessionCipher.decryptWhisperMessage(bodyBinary, 'binary');

      return new Uint8Array(keyBuffer);
    });
	viaSignal = true;
	}
  }

  if (!aesKey || aesKey.byteLength !== 32) {
    throw new Error(`Clé AES message invalide : ${aesKey?.byteLength || 0} octets`);
  }

// Sauvegarde APRÈS validation de la clé, et seulement si elle vient de Signal
  if (viaSignal && messageId) {
    saveSelfWrap(`/api/messages/${messageId}/keyforself`, aesKey);
  }
  return await aesDecryptText(
    base64ToBuffer(encryptedText),
    aesKey,
    base64ToBuffer(e2eeData.textIv)
  );
}

async function decryptPrivateFile(
  fileData,
  senderId,
  currentUserId,
  fileId
) {
  if (!fileData.encryptedFile) {
    throw new Error('Fichier chiffré manquant');
  }

  if (!fileData.fileIv) {
    throw new Error('IV fichier manquant');
  }

  const fileKey = await resolveFileKey(
  fileData,
  senderId,
  currentUserId,
  fileId,
  false // isGroup
);

   

  return await aesDecryptBytes(
    base64ToBuffer(fileData.encryptedFile),
    fileKey,
    base64ToBuffer(fileData.fileIv)
  );
}
export async function decryptFile(
  fileData,
  senderId,
  currentUserId,
  fileId,
selfWrapUrl
) {
  if (!fileData?.e2ee) {
    throw new Error('Fichier non E2EE');
  }

  // 👥 Groupe
  if (fileData.e2eeGroup === true) {
    return decryptGroupFile(
      fileData,
      senderId,
      currentUserId,
      fileId
    );
  }

  // 👤 Privé
  return decryptPrivateFile(
    fileData,
    senderId,
    currentUserId,
    fileId
  );
}

async function ensureSignalSession(userId) {
  const address = new SignalProtocolAddress(
    String(userId),
    1
  );

  return withSignalLock(address.toString(), async () => {
    let sessionRecord =
      await signalStore.loadSession(address.toString());

    if (!sessionRecord) {
      await buildSessionWithContactUnlocked(
        String(userId)
      );

      sessionRecord =
        await signalStore.loadSession(address.toString());
    }

    if (!sessionRecord) {
      throw new Error(
        `Session Signal introuvable pour ${address.toString()}`
      );
    }

    return address;
  });
}
export async function buildSessionWithContactUnlocked(contactUserId) {
  const address = new SignalProtocolAddress(
    String(contactUserId),
    1
  );

const addressStr = address.toString();


await signalStore.remove(`session_${addressStr}`);
await signalStore.remove(`identity_${addressStr}`);

  const res = await api.get(
    `/api/signal/prekey-bundle/${contactUserId}`
  );

  const bundle = res.data;

const localIdentity = await signalStore.get('identityKey');

  const sessionBuilder =
    new SessionBuilder(signalStore, address);

  const preKeyBundle = {
    registrationId: bundle.registrationId,
    identityKey: base64ToBuffer(bundle.identityKey),

    signedPreKey: {
      keyId: bundle.signedPreKey.keyId,
      publicKey: base64ToBuffer(
        bundle.signedPreKey.publicKey
      ),
      signature: base64ToBuffer(
        bundle.signedPreKey.signature
      ),
    },
  };

  if (bundle.preKey) {
    preKeyBundle.preKey = {
      keyId: bundle.preKey.keyId,
      publicKey: base64ToBuffer(
        bundle.preKey.publicKey
      ),
    };
  }
const identityKeyBuffer = base64ToBuffer(bundle.identityKey);
const signedPreKeyPublicBuffer = base64ToBuffer(
  bundle.signedPreKey.publicKey
);
const signatureBuffer = base64ToBuffer(
  bundle.signedPreKey.signature
);




  await sessionBuilder.processPreKey(preKeyBundle);

}
export async function encryptFile(recipientUserId, plaintext) {
  const address = await ensureSignalSession(recipientUserId);
  const { ciphertext, iv, key } = await aesEncryptBytes(plaintext);

  return withSignalLock(address.toString(), async () => {
    const sessionCipher = new SessionCipher(signalStore, address);
    const signalEncryptedKey = await sessionCipher.encrypt(
      key.buffer.slice(key.byteOffset, key.byteOffset + key.byteLength)
    );
    const selfEncrypted = await selfEncryptKey(key);
    return {
      encryptedFile: bufferToBase64(ciphertext),
      fileIv: bufferToBase64(iv),
      keyForRecipient: binaryStringToBase64(signalEncryptedKey.body),
      keyForRecipientType: signalEncryptedKey.type,
      keyForSender: bufferToBase64(selfEncrypted.encrypted),
      keyForSenderIv: bufferToBase64(selfEncrypted.iv),
    };
  });
}
export async function encryptKeyForUser(userId, aesKey) {
  const address = await ensureSignalSession(userId);

  return withSignalLock(address.toString(), async () => {
    const sessionCipher = new SessionCipher(signalStore, address);
    const keyBuffer = aesKey.buffer.slice(
      aesKey.byteOffset,
      aesKey.byteOffset + aesKey.byteLength
    );
    const encrypted = await sessionCipher.encrypt(keyBuffer);
    return {
      encryptedKey: binaryStringToBase64(encrypted.body),
      keyType: encrypted.type,
    };
  });
}


const _locks = new Map();

export function withSignalLock(addressKey, fn) {
  const prev = _locks.get(addressKey) || Promise.resolve();
  const next = prev.then(fn, fn);
  _locks.set(addressKey, next.catch(() => {}));
  return next;
}
export async function decryptKeyFromUser(encryptedKey, keyType, senderUserId) {
  const address = new SignalProtocolAddress(String(senderUserId), 1);
  return withSignalLock(address.toString(), async () => {
    const sessionCipher = new SessionCipher(signalStore, address);
    const bodyBinary = base64ToBinaryString(encryptedKey);
    const keyBuffer = Number(keyType) === 3
      ? await sessionCipher.decryptPreKeyWhisperMessage(bodyBinary, 'binary')
      : await sessionCipher.decryptWhisperMessage(bodyBinary, 'binary');
    const aesKey = new Uint8Array(keyBuffer);
    if (aesKey.byteLength !== 32) throw new Error(`Clé AES invalide : ${aesKey.byteLength} octets`);
    return aesKey;
  });
}
export async function encryptGroupFile(
  fileBuffer,
  members,
  currentUserId
) {
  if (!(fileBuffer instanceof ArrayBuffer)) {
    throw new Error('encryptGroupFile: fileBuffer doit être un ArrayBuffer');
  }

  if (!Array.isArray(members)) {
    throw new Error('encryptGroupFile: members doit être un tableau');
  }

  // 🔐 Une seule clé AES pour tout le fichier
  const fileKey = crypto.getRandomValues(
    new Uint8Array(32)
  );

  // 🔐 IV AES-GCM
  const fileIv = crypto.getRandomValues(
    new Uint8Array(12)
  );

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    fileKey,
    {
      name: 'AES-GCM',
      length: 256
    },
    false,
    ['encrypt']
  );

  const encrypted = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: fileIv
    },
    cryptoKey,
    new Uint8Array(fileBuffer)
  );

  const keyForMembers = [];

  for (const member of members) {
    const memberId = member?._id || member;

    if (!memberId) continue;

    // Pas besoin de s'envoyer la clé via Signal :
    // le sender utilise son enveloppe locale.
    if (String(memberId) === String(currentUserId)) {
      continue;
    }
const address = new SignalProtocolAddress(
  String(memberId),
  1
);

    

const wrapped = await encryptKeyForUser(
  String(memberId),
  fileKey
);


    keyForMembers.push({
      userId: String(memberId),
      encryptedKey: wrapped.encryptedKey,
      keyType: wrapped.keyType
    });
  }

  // 🔐 Copie destinée à l'expéditeur
  const selfEncrypted = await selfEncryptKey(fileKey);

  return {
    encryptedFile: bufferToBase64(
      new Uint8Array(encrypted)
    ),

    fileIv: bufferToBase64(fileIv),

    keyForMembers,

    keyForSender: bufferToBase64(
      selfEncrypted.encrypted
    ),

    keyForSenderIv: bufferToBase64(
      selfEncrypted.iv
    )
  };
}

export async function decryptGroupFile(
  fileData,
  senderId,
  currentUserId,
  fileId
) {
  if (!fileData?.e2ee) {
    throw new Error('Fichier non E2EE');
  }

  if (fileData.e2eeGroup !== true) {
    throw new Error('Ce fichier n’est pas un fichier E2EE groupe');
  }

  if (!fileData.encryptedFile) {
    throw new Error('Fichier chiffré manquant');
  }

  if (!fileData.fileIv) {
    throw new Error('IV fichier manquant');
  }

  // 1. Chercher la clé AES en cache
  const fileKey = await resolveFileKey(
    fileData,
    senderId,
    currentUserId,
    fileId,
    true // isGroup
  );


    
  // 2. Déchiffrer réellement le fichier
  return await aesDecryptBytes(
    base64ToBuffer(fileData.encryptedFile),
    fileKey,
    base64ToBuffer(fileData.fileIv)
  );
}

export async function encryptPayloadForUser(userId, bytes) {
  const address = await ensureSignalSession(userId);
  return withSignalLock(address.toString(), async () => {
    const cipher = new SessionCipher(signalStore, address);
    const enc = await cipher.encrypt(toArrayBuffer(bytes));
    return { body: binaryStringToBase64(enc.body), type: enc.type };
  });
}

export async function decryptPayloadFromUser(bodyB64, type, senderUserId) {
  const address = new SignalProtocolAddress(String(senderUserId), 1);
  return withSignalLock(address.toString(), async () => {
    const cipher = new SessionCipher(signalStore, address);
    const bin = base64ToBinaryString(bodyB64);
    const buf = Number(type) === 3
      ? await cipher.decryptPreKeyWhisperMessage(bin, 'binary')
      : await cipher.decryptWhisperMessage(bin, 'binary');
    return new Uint8Array(buf);
  });
}

async function deleteSessionsFor(userId) {
  const id = String(userId);
  await signalStore.removeSession(`${id}.1`);
  //console.log('[identity] session supprimée pour', id);
}

export async function getE2EEReady(userIds) {
  const { data } = await api.post('/api/signal/availability', { userIds });
  const ready = new Set(data.ready.map(String));

  await Promise.all([...ready].map(async (id) => {
    await signalStore.put(`peerE2EE_${id}`, true);

    const current = data.identities?.[id];
    if (!current) return;
    const known = await signalStore.get(`identity:${id}`);
    if (known && known !== current) {
      await deleteSessionsFor(id);          // identité changée : session périmée
    }
    await signalStore.put(`identity:${id}`, current);
  }));

  return ready;
}
